import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";

type CurrencyCode = "BRL" | "USD" | "EUR";

type TextFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  uppercase?: boolean;

  /** Input nativo: "text" (default) ou "date" — use "date" só se quiser o picker nativo */
  inputType?: "text" | "date";

  /** Input numérico com máscara monetária (ex.: R$ 1.234,56) */
  contabil?: boolean;
  currency?: CurrencyCode;
  currencyLocale?: string;

  /** Bloqueio */
  locked?: boolean;
  disabled?: boolean;

  /** Máscara de data dd/MM/yyyy (modo texto) */
  date?: boolean;

  /** Campo numérico que aceita sufixos (ex.: "10.5 kg") */
  numericWithUnit?: boolean;

  /** ---- Dropdown ---- */
  dropdown?: boolean;
  options?: string[];
  noResultsText?: string;

  // Botão 1
  button?: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
  buttonWidth?: number | string;
  buttonDisabled?: boolean;
  buttonVariant?: "primary" | "secondary";

  // Botão 2
  secondButton?: boolean;
  secondButtonText?: string;
  onSecondButtonClick?: () => void;
  secondButtonWidth?: number | string;
  secondButtonDisabled?: boolean;
  secondButtonVariant?: "primary" | "secondary";
};

const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  style = {},
  uppercase = false,

  inputType = "text",

  // contábil
  contabil = false,
  currency = "BRL",
  currencyLocale,

  // bloqueios
  locked = false,
  disabled = false,

  // máscaras
  date = false,
  numericWithUnit = false,

  // dropdown
  dropdown,
  options,
  noResultsText = "Sem opções",

  // Botão 1
  button = false,
  buttonText = "Ação",
  onButtonClick,
  buttonWidth = 100,
  buttonDisabled = false,
  buttonVariant = "primary",

  // Botão 2
  secondButton = false,
  secondButtonText = "Ação 2",
  onSecondButtonClick,
  secondButtonWidth = 100,
  secondButtonDisabled = false,
  secondButtonVariant = "secondary",
}) => {
  // ======= Estado dropdown =======
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Modo dropdown efetivo
  const isDropdown = dropdown ?? (Array.isArray(options) && options.length > 0);
  const isDisabled = locked || disabled;

  // ======= Estilos base =======
  const outerContainerStyle: React.CSSProperties = {
    marginBottom: 0,
    marginLeft: style.marginLeft ?? 0,
    opacity: isDisabled ? 0.7 : 1,
  };
  const rowStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    alignItems: "stretch",
    width: style.width ?? "100%",
  };
  const inputContainerStyle: React.CSSProperties = {
    flex: 1,
    height: "40px",
    backgroundColor: isDisabled ? "#F3F3F3" : "#FFFFFF",
    border: "1px solid #8A8A8A",
    borderRadius: 2,
    boxSizing: "border-box",
    position: "relative",
    cursor: isDisabled ? "not-allowed" : isDropdown ? "pointer" : "text",
  };

  // ======= Helpers =======
  const onlyDigits = (s: string) => s.match(/\d+/g)?.join("") ?? "";

  const makeButtonStyle = (
    w: number | string,
    disabledBtn: boolean,
    variant: "primary" | "secondary"
  ): React.CSSProperties => {
    const width = typeof w === "number" ? `${w}px` : w;
    const base: React.CSSProperties = {
      width,
      height: 40,
      borderRadius: 4,
      fontFamily: "Inter, sans-serif",
      fontSize: 14,
      fontWeight: 500,
      padding: "0 12px",
      whiteSpace: "nowrap",
      cursor: disabledBtn ? "not-allowed" : "pointer",
    };
    if (variant === "primary") {
      return {
        ...base,
        border: primaryColor,
        background: disabledBtn ? "#7aa2d4" : primaryColor,
        color: "#fff",
      };
    }
    return {
      ...base,
      border: "1px solid #8A8A8A",
      background: disabledBtn ? "#efefef" : "#ffffff",
      color: "#124280",
    };
  };

  // moeda dinâmica
  const formatCurrency = (
    digits: string,
    curr: CurrencyCode,
    locale?: string
  ) => {
    const cents = digits === "" ? 0 : Number(digits);
    const asNumber = cents / 100;
    return new Intl.NumberFormat(locale ?? "pt-BR", {
      style: "currency",
      currency: curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(asNumber);
  };
  const digitsToNumericStr = (digits: string, locale?: string) => {
    if (digits === "") return "";
    const cents = Number(digits);
    const asNumber = cents / 100;
    return asNumber.toLocaleString(locale ?? "pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ======= Data - máscara texto (dd/MM/yyyy) =======
  const formatDateMask = (raw: string) => {
    const d = onlyDigits(raw).slice(0, 8);
    const dd = d.slice(0, 2);
    const mm = d.slice(2, 4);
    const yyyy = d.slice(4, 8);
    if (d.length <= 2) return dd;
    if (d.length <= 4) return `${dd}/${mm}`;
    return `${dd}/${mm}/${yyyy}`;
  };

  // mapeia "k-ésimo dígito" -> posição de caret na string mascarada
  const caretPosForDigitCount = (masked: string, k: number) => {
    if (k <= 0) return 0;
    let count = 0;
    for (let i = 0; i < masked.length; i++) {
      if (/\d/.test(masked[i])) {
        count++;
        if (count === k) return i + 1;
      }
    }
    return masked.length;
  };

  // ======= Data - nativo (yyyy-MM-dd) =======
  const toDateInputValue = (raw: string) => {
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw; // yyyy-MM-dd
    const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); // dd/MM/yyyy -> yyyy-MM-dd
    if (m) {
      const [, dd, mm, yyyy] = m;
      return `${yyyy}-${mm}-${dd}`;
    }
    return "";
  };

  // display contextual
  const displayValue = useMemo(() => {
    if (isDropdown) return uppercase ? value.toUpperCase() : value;

    // Modo data (texto mascarado)
    if (date) return formatDateMask(value);

    // Modo picker nativo
    if (inputType === "date" && !date) return toDateInputValue(value);

    // Monetário
    if (contabil) {
      const digits = onlyDigits(value);
      return formatCurrency(digits, currency, currencyLocale);
    }

    return uppercase ? value.toUpperCase() : value;
  }, [
    isDropdown,
    value,
    date,
    contabil,
    uppercase,
    currency,
    currencyLocale,
    inputType,
  ]);

  // ======= onChange com preservação do cursor (modo date) =======
  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled || isDropdown) return;

    const rawNext = e.target.value;

    // Campo data como texto (dd/MM/yyyy com máscara incremental)
    if (date) {
      const el = e.target;
      const nextDigits = onlyDigits(rawNext);
      const nextMasked = formatDateMask(rawNext);

      // dígitos antes do caret no que o usuário digitou
      const caretIn = el.selectionStart ?? rawNext.length;
      const digitsBeforeCaret = onlyDigits(rawNext.slice(0, caretIn)).length;

      // Atualiza o valor
      onChange(nextMasked);

      // Reposiciona o caret após pintar
      requestAnimationFrame(() => {
        const pos = caretPosForDigitCount(nextMasked, digitsBeforeCaret);
        if (inputRef.current) {
          const clamped = Math.max(
            0,
            Math.min(pos, inputRef.current.value.length)
          );
          inputRef.current.setSelectionRange(clamped, clamped);
        }
      });
      return;
    }

    // Modo picker nativo ("date" sem máscara)
    if (inputType === "date") {
      onChange(rawNext);
      return;
    }

    // Monetário
    if (contabil) {
      const digits = onlyDigits(rawNext);
      const numericStr = digitsToNumericStr(digits, currencyLocale);
      onChange(numericStr);
      return;
    }

    // Numérico com unidade
    if (numericWithUnit) {
      const cleaned = rawNext.replace(/,/g, ".");
      onChange(cleaned);
      return;
    }

    // Texto comum
    onChange(uppercase ? rawNext.toUpperCase() : rawNext);
  };

  const dropdownDisplayText = useMemo(() => {
    if (value) return uppercase ? value.toUpperCase() : value;
    return placeholder || "Selecione...";
  }, [value, placeholder, uppercase]);

  // click fora → fecha dropdown
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // navegação por teclado no dropdown
  const opts = Array.isArray(options) ? options : [];
  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1);
      return;
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!open || !isDropdown || isDisabled) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next = prev + 1;
        return next >= opts.length ? 0 : next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? Math.max(0, opts.length - 1) : next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < opts.length) {
        const v = opts[focusedIndex];
        onChange(uppercase ? v.toUpperCase() : v);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const { theme } = useTheme();

  // cor base do tema (fallback para o azul antigo)
  const primaryColor = theme?.primary;

  // Ícone de seta (fina, completa)
  const ChevronDownSVG = (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="#686D79"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div ref={rootRef} style={outerContainerStyle} onKeyDown={handleKeyDown}>
      <div style={{ marginBottom: 7, marginTop: 20 }}>
        <label
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 300,
            color: "#686D79FF",
          }}
        >
          {label}
        </label>
      </div>

      <div style={rowStyle}>
        {/* Campo / Dropdown */}
        <div
          style={inputContainerStyle}
          onClick={() => {
            if (isDisabled) return;
            if (isDropdown) setOpen((v) => !v);
          }}
        >
          {!isDropdown ? (
            <input
              ref={inputRef}
              type={inputType === "date" && !date ? "date" : "text"}
              value={
                inputType === "date" && !date
                  ? displayValue /* yyyy-MM-dd para nativo */
                  : displayValue /* texto mascarado | monetário | etc. */
              }
              onChange={handleTextInputChange}
              placeholder={
                placeholder ??
                (date && !(inputType === "date" && !date)
                  ? "DD/MM/AAAA"
                  : undefined)
              }
              disabled={isDisabled}
              onPaste={(e) => {
                if (isDisabled) return;

                // No modo data (texto), aceitar colagens variadas:
                if (date) {
                  const text = e.clipboardData.getData("text") || "";
                  const masked = formatDateMask(text);
                  e.preventDefault();
                  // Simula change com o valor já mascarado
                  const fakeEvent = {
                    target: {
                      value: masked,
                      selectionStart: masked.length,
                    },
                  } as unknown as React.ChangeEvent<HTMLInputElement>;
                  handleTextInputChange(fakeEvent);
                  return;
                }

                // No modo nativo de date, permitir dd/MM/yyyy e converter
                if (inputType === "date" && !date) {
                  const text = e.clipboardData.getData("text") || "";
                  const m = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  if (m) {
                    e.preventDefault();
                    const [, dd, mm, yyyy] = m;
                    onChange(`${yyyy}-${mm}-${dd}`);
                  }
                }
              }}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                outline: "none",
                padding: "0 8px",
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                backgroundColor: "transparent",
                boxSizing: "border-box",
                color: isDisabled ? "#666" : "#000",
                cursor: isDisabled ? "not-allowed" : "text",
              }}
            />
          ) : (
            <>
              <div
                style={{
                  padding: "0 28px 0 8px",
                  lineHeight: "40px",
                  fontFamily: "Inter, sans-serif",
                  fontSize: 14,
                  color: value ? "#000" : "#999",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {dropdownDisplayText}
              </div>

              {!isDisabled && (
                <div
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                >
                  {ChevronDownSVG}
                </div>
              )}

              {open && !isDisabled && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderTop: "none",
                    borderRadius: "0 0 2px 2px",
                    maxHeight: 220,
                    overflowY: "auto",
                    zIndex: 10,
                    boxShadow:
                      "0 6px 12px rgba(10, 31, 68, 0.10), 0 2px 6px rgba(10, 31, 68, 0.06)",
                  }}
                >
                  {opts.length === 0 ? (
                    <div
                      style={{
                        padding: 10,
                        color: "#666",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                      }}
                    >
                      {noResultsText}
                    </div>
                  ) : (
                    opts.map((opt, i) => {
                      const selected = opt === value;
                      const focused = i === focusedIndex;
                      return (
                        <div
                          key={`${opt}-${i}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (isDisabled) return;
                            onChange(uppercase ? opt.toUpperCase() : opt);
                            setOpen(false);
                          }}
                          onMouseEnter={() => setFocusedIndex(i)}
                          style={{
                            padding: "8px 10px",
                            cursor: "pointer",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14,
                            backgroundColor: focused
                              ? "rgba(18,66,128,0.08)"
                              : selected
                              ? "#f5f7fb"
                              : "#fff",
                            color: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {opt}
                          </span>
                          {selected && (
                            <span
                              style={{
                                fontSize: 10,
                                color: primaryColor,
                                marginLeft: 8,
                              }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Botões auxiliares */}
        {button && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!buttonDisabled) onButtonClick?.();
            }}
            disabled={buttonDisabled}
            style={makeButtonStyle(buttonWidth, buttonDisabled, buttonVariant)}
          >
            {buttonText}
          </button>
        )}

        {secondButton && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!secondButtonDisabled) onSecondButtonClick?.();
            }}
            disabled={secondButtonDisabled}
            style={makeButtonStyle(
              secondButtonWidth,
              secondButtonDisabled,
              secondButtonVariant
            )}
          >
            {secondButtonText}
          </button>
        )}
      </div>
    </div>
  );
};

export default TextField;
